import React, { useState } from 'react';
import { useCrudPage } from '../../hooks/useCrudPage';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { api } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  alpha,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  FilterList,
  CheckCircle,
  Cancel,
  Schedule,
  Warning,
  Visibility,
  TrendingUp,
  SmartToy,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'approved' | 'rejected' | 'in_review';
  priority: 'low' | 'medium' | 'high' | 'critical';
  agent: string;
  created_at: string;
  risk_level?: string;
  estimated_impact?: string;
}

const TaskListPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');

  const {
    items: tasks,
    loading,
    error,
    success,
    fetchItems,
    clearSuccess,
  } = useCrudPage<Task>({
    listFn: api.tasks.list,
  });

  // Mock data removed - using API

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'in_review': return 'info';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  const filteredTasks = (tasks || []).filter((task: Task) => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    const matchesAgent = filterAgent === 'all' || task.agent === filterAgent;
    const matchesTab = activeTab === 0 ? task.status === 'pending' :
      activeTab === 1 ? task.status === 'in_review' :
        activeTab === 2 ? task.status === 'approved' :
          task.status === 'rejected';

    return matchesSearch && matchesPriority && matchesAgent && matchesTab;
  });

  const taskCounts = {
    pending: (tasks || []).filter((t: Task) => t.status === 'pending').length,
    in_review: (tasks || []).filter((t: Task) => t.status === 'in_review').length,
    approved: (tasks || []).filter((t: Task) => t.status === 'approved').length,
    rejected: (tasks || []).filter((t: Task) => t.status === 'rejected').length,
  };

  return (
    <Box>
      {error && <ErrorAlert error={error} onRetry={fetchItems} />}

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          HITL Task Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and approve AI agent recommendations
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                    {taskCounts.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Review
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                  <Schedule sx={{ color: theme.palette.warning.main }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                    {taskCounts.in_review}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Review
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                  <Visibility sx={{ color: theme.palette.info.main }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                    {taskCounts.approved}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Approved
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                  <CheckCircle sx={{ color: theme.palette.success.main }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                    {taskCounts.rejected}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rejected
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                  <Cancel sx={{ color: theme.palette.error.main }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Agent</InputLabel>
                <Select
                  value={filterAgent}
                  onChange={(e) => setFilterAgent(e.target.value)}
                  label="Agent"
                >
                  <MenuItem value="all">All Agents</MenuItem>
                  <MenuItem value="Strategic Forecaster">Strategic Forecaster</MenuItem>
                  <MenuItem value="Resource Optimizer">Resource Optimizer</MenuItem>
                  <MenuItem value="Game Theorist">Game Theorist</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label={`Pending (${taskCounts.pending})`} />
          <Tab label={`In Review (${taskCounts.in_review})`} />
          <Tab label={`Approved (${taskCounts.approved})`} />
          <Tab label={`Rejected (${taskCounts.rejected})`} />
        </Tabs>

        {/* Task Table */}
        {loading ? (
          <LoadingState message="Loading tasks..." />
        ) : filteredTasks.length === 0 ? (
          <EmptyState
            title="No tasks found"
            description="No tasks match your current filters"
            icon={<SmartToy sx={{ fontSize: 64, color: 'text.disabled' }} />}
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Risk</TableCell>
                  <TableCell>Impact</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        No tasks found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTasks.map((task) => (
                    <TableRow
                      key={task.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {task.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {task.description.substring(0, 80)}...
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<SmartToy />}
                          label={task.agent}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.priority.toUpperCase()}
                          size="small"
                          sx={{
                            bgcolor: alpha(getPriorityColor(task.priority), 0.1),
                            color: getPriorityColor(task.priority),
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={task.risk_level} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{task.estimated_impact}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(task.created_at).toLocaleDateString('en-US')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/tasks/${task.id}`);
                          }}
                        >
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <SuccessSnackbar
        open={!!success}
        message={success || ''}
        onClose={clearSuccess}
      />
    </Box>
  );
};

export default TaskListPage;
